import {
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseContentItem,
  BaseFilter,
  ListResponseOfBaseContentItem,
} from "services/KnowfullyAdminClient";
import { Mode } from "types/common-types";
import contextStore from "stores/contextStore";

type SelectContentProps = {
  value?: string | null;
  mode: Mode;
  onChange: (id: string | null) => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<BaseContentItem>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const ContentSearch = ({
  value: contentKey,
  mode,
  onChange,
  required,
  label,
  ...rest
}: SelectContentProps) => {
  const { formatMessage: t } = useIntl();
  const { contentService } = useGlobalContext();
  const queryClient = useQueryClient();
  const { organizationId } = contextStore.useState();
  const [content, setContent] = useState<string | null | undefined>("");
  const [contentItem, setContentItem] = useState<BaseContentItem | null>(null);
  const [searchEnabled, setSearchEnabled] = useBoolean(false);
  const [initialKey, setInitialKey] = useState<string | null | undefined>(null);

  useEffect(() => {
    setContent(contentKey);
  }, [contentKey]);

  //warning disabled since it's expected to have an empty dependency
  //array here for invoking this line once
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setInitialKey(contentKey), []);

  const fetchContents = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfBaseContentItem>(
      ["contents", organizationId, searchTerm],
      () =>
        contentService.getContents(
          AdminListRequestOfBaseFilter.fromJS({
            pageNumber: 0,
            pageSize: 20,
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
            sortField: "name",
          })
        )
    );

    return { result, forSearchTerm: searchTerm };
  };

  const onChangeContent = (content: BaseContentItem | null) => {
    if (content !== null) {
      setContentItem(content);
      onChange(content?.name ?? null);
    }
  };

  return (
    <>
      {!searchEnabled &&
      mode === Mode.Update &&
      initialKey !== null &&
      initialKey !== undefined &&
      initialKey !== "" ? (
        <>
          <TextField
            fullWidth
            variant="standard"
            value={content ?? ""}
            label={label}
            InputProps={{
              endAdornment: (
                <InputAdornment position="start">
                  <Tooltip
                    title={t({
                      id: "enableContentSearchMessage",
                    })}
                    style={{ textAlign: "left" }}
                  >
                    <IconButton
                      onClick={() => {
                        setSearchEnabled.setTrue();
                        setContentItem(null);
                        onChange(null);
                      }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </>
      ) : (
        <>
          <AsyncAutocompleteWithQuery
            {...rest}
            value={contentItem}
            labelField="name"
            idField="name"
            fetchData={fetchContents}
            failMessage={t({ id: "selectContentFetchFailed" })}
            onChange={onChangeContent}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant="standard"
                InputLabelProps={{ required }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <div>
                      {params.InputProps.endAdornment}
                      {mode === Mode.Update &&
                        initialKey !== null &&
                        initialKey !== undefined &&
                        initialKey !== "" && (
                          <InputAdornment position="start">
                            <Button
                              autoFocus
                              onClick={() => {
                                setContent(initialKey);
                                setSearchEnabled.setFalse();
                              }}
                              color="primary"
                            >
                              <T id="cancelSearchButton" />
                            </Button>
                          </InputAdornment>
                        )}
                    </div>
                  ),
                }}
                fullWidth
              />
            )}
          />
        </>
      )}
    </>
  );
};

export default ContentSearch;
